import React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../../theming/useTheme';
import { DropDownMenuLink } from './DropDownMenuLink';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { SemanticTagThemeProps } from '../../../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../../theming/fontSizeTranslator';

interface DropDownMenuProps {
  configThemeKey: string;
}

export interface DropDownMenuBaseThemeProps extends SemanticTagThemeProps {
  menuBackgroundColor: string;
  linkColor: string;
  hoverColor: string;
  activeColor: string;
  baseUnit: BaseUnit;
  boxShadowTypeNormal: string;
}

interface MenuLinkProps {
  text: string;
  href: string;
}

export interface DropDownMenuBaseProps {
  configThemeKey?: string;
  menuLinks: MenuLinkProps[];
  highlightActive?: boolean;
  activeEndpoint: string;
}

type ThemedDropDownMenu = DropDownMenuProps & DropDownMenuBaseProps;

const getStyle = (theme: DropDownMenuBaseThemeProps) => {
  const { baseUnit, menuBackgroundColor } = theme;

  return StyleSheet.create({
    menuWrapper: {
      position: 'absolute',
      top: '100%',
      width: '100%',
      minWidth: '160px',
      boxSizing: 'border-box',
      background: menuBackgroundColor,
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `${baseUnit / 4}px ${baseUnit / 2}px`,
      },
    },
  });
};

const getLinksList = (
  menuLinks: MenuLinkProps[],
  linkColor: string,
  hoverColor: string,
  activeColor: string,
  baseUnit: BaseUnit,
  configThemeKey: string | undefined,
  activeEndpoint: string,
  fontSizesStyles: TranslatedFontSize,
) => {
  const isActive = (link: MenuLinkProps) => link.href === activeEndpoint;
  return menuLinks.map((link, index) => (
    <DropDownMenuLink
      key={index}
      href={link.href}
      text={link.text}
      linkColor={linkColor}
      hoverColor={hoverColor}
      activeColor={activeColor}
      baseUnit={baseUnit}
      isActive={isActive(link)}
      configThemeKey={configThemeKey}
      fontSizesStyles={fontSizesStyles}
    />
  ));
};

export const DropDownMenu: React.FunctionComponent<ThemedDropDownMenu> = props => {
  const { menuLinks, configThemeKey, activeEndpoint } = props;
  const themeProps = useTheme<DropDownMenuBaseThemeProps>(configThemeKey);
  const { boxShadowTypeNormal, baseUnit, fontSizesStyles, linkColor, hoverColor, activeColor } = themeProps;
  const style = getStyle(themeProps);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });
  return (
    <ErrorBoundary>
      <ul className={css(style.menuWrapper, boxShadowStyle.style)}>
        {getLinksList(menuLinks, linkColor, hoverColor, activeColor, baseUnit, configThemeKey, activeEndpoint, fontSizesStyles)}
      </ul>
    </ErrorBoundary>
  );
};
